import type { AttemptInput } from '@dcs/contracts';
import type { KeyValueStorage } from '@dcs/platform/ports';

/**
 * Offline queue of finished exams (F6 T-2.2, AC-2.4). A signed-in user's result is stored here first
 * and sent from here, so a lost connection, an expired session or a server hiccup never loses it and
 * never double-writes it:
 *
 * - The queue lives in the platform storage port (`KeyValueStorage`), so it survives a reload. It is
 *   read from storage on every operation (a second tab sees the same list); sending is single-flight
 *   inside one tab, and a duplicate send from two tabs is harmless (same `idempotencyKey`, the server
 *   answers 200 with the stored row).
 * - The `idempotencyKey` is made once, when the exam finishes, and stored inside the item: a retry or
 *   a reload sends the same body. `enqueue` is idempotent by key.
 * - Items carry their owner (user id) and are only sent while that user is signed in, so a result is
 *   never uploaded to another account on a shared device.
 * - Before a run sends anything it asks `session()` who is signed in *now* (a fresh `/api/me`, not a
 *   store another tab may have outdated): only the items of that user are sent; another user or
 *   nobody stops the run, an error counts as "no answer".
 * - Outcomes of a send: success (a 200 replay included) removes the item; no answer (offline) or 401
 *   keeps it without counting a failure; 408, 429, 5xx and unexpected errors keep it and back off
 *   exponentially; 403 and 404 (a proxy or the API not deployed yet) are retried
 *   `MAX_CLIENT_ERROR_RETRIES` times with backoff; any other 4xx (409 key reuse, 422 invalid ...)
 *   moves it to the dead-letter list at once and reports it, instead of retrying forever.
 * - Privacy: `purge(owner)` (sign-out, account deletion) removes that owner's items and dead letters.
 *   Dead letters older than 7 days are dropped, and so are queued items of *other* owners older than
 *   7 days: they cannot leave this device unless that user signs in again here.
 * - A stored value this code cannot read (not JSON, or a version it does not know) is never
 *   overwritten: the queue works in memory for the rest of the session and reports it once.
 * - `subscribeSaved` tells listeners (the history list's query cache, T-2.3) about every attempt the
 *   server accepted from this tab.
 *
 * This module imports no HTTP client: the sender is injected, so the app shell can carry the queue
 * while `@dcs/api` stays a lazy chunk.
 */

export const QUEUE_STORAGE_KEY = 'dcs.attempts.queue.v1';
export const MAX_QUEUED = 50;
export const MAX_DEAD = 20;
export const BACKOFF_BASE_MS = 30_000;
export const BACKOFF_MAX_MS = 15 * 60_000;
export const MAX_CLIENT_ERROR_RETRIES = 3;
/** Dead letters, and queued items of other owners, are dropped after this long. */
export const RETENTION_MS = 7 * 24 * 60 * 60_000;

export interface QueuedAttempt {
  readonly input: AttemptInput;
  /** User id the result belongs to. */
  readonly owner: string;
  /** Failed sends so far (offline and 401 do not count). */
  readonly attempts: number;
  /** Epoch ms before which the item is not sent again. */
  readonly nextAt: number;
  readonly queuedAt: number;
}

export interface DeadAttempt {
  readonly input: AttemptInput;
  readonly owner: string;
  /** HTTP status that ended it. */
  readonly status: number;
  readonly code: string;
  readonly at: number;
}

/** `purged`: removed by a sign-out or an account deletion; it must not be shown as saved. */
export type AttemptStatus = 'queued' | 'saved' | 'dead' | 'purged' | 'unknown';

/** Who the server says is signed in right now. `error`: it could not be asked (offline, 5xx). */
export type SessionCheck =
  | { readonly kind: 'user'; readonly id: string }
  | { readonly kind: 'anonymous' }
  | { readonly kind: 'error' };

export type FlushStop = 'offline' | 'unauthorized' | 'backoff' | null;

export interface FlushResult {
  readonly sent: number;
  readonly dropped: number;
  /** Why the run ended early; `null` when every due item was handled. */
  readonly stoppedBy: FlushStop;
}

export type AttemptSender = (input: AttemptInput) => Promise<unknown>;

export interface AttemptQueueOptions {
  readonly storage: KeyValueStorage;
  readonly send: AttemptSender;
  /** Epoch ms; default `Date.now`. */
  readonly now?: () => number;
  /** Error sink (the app's `ErrorReporter.report`). Never receives the payload. */
  readonly report?: (error: unknown) => void;
  /** Delay before the next try after `attempts` failures. */
  readonly backoff?: (attempts: number) => number;
  /** Fresh answer to "who is signed in?"; asked once per run that has something to send. */
  readonly session?: () => Promise<SessionCheck>;
}

export interface AttemptQueue {
  /** Adds a result for `owner`. `false` when its key is already queued, dead or saved. */
  enqueue(input: AttemptInput, owner: string): boolean;
  /** Sends every due item of `owner`, oldest first. Single-flight per owner. */
  flush(owner: string): Promise<FlushResult>;
  /**
   * Removes the queued items and dead letters of `owner` (all owners when omitted). A stored value
   * this code cannot read is left alone, except by a purge of everything.
   */
  purge(owner?: string): void;
  /** Called with each attempt the server accepted from this tab (a 200 replay included). */
  subscribeSaved(listener: (attempt: AttemptInput) => void): () => void;
  statusOf(key: string): AttemptStatus;
  pending(owner?: string): readonly QueuedAttempt[];
  dead(): readonly DeadAttempt[];
  /** Ms until the next item of `owner` is due; `null` when there is none. */
  nextDueIn(owner: string): number | null;
  /** Changes on every mutation (for `useSyncExternalStore`). */
  version(): number;
  subscribe(listener: () => void): () => void;
}

interface Persisted {
  readonly v: 1;
  readonly items: readonly QueuedAttempt[];
  readonly dead: readonly DeadAttempt[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const hasKey = (value: unknown): boolean =>
  isRecord(value) && typeof value.idempotencyKey === 'string' && value.idempotencyKey !== '';

function isQueued(value: unknown): value is QueuedAttempt {
  return (
    isRecord(value) &&
    hasKey(value.input) &&
    typeof value.owner === 'string' &&
    typeof value.attempts === 'number' &&
    typeof value.nextAt === 'number' &&
    typeof value.queuedAt === 'number'
  );
}

function isDead(value: unknown): value is DeadAttempt {
  return (
    isRecord(value) &&
    hasKey(value.input) &&
    typeof value.owner === 'string' &&
    typeof value.status === 'number' &&
    typeof value.code === 'string' &&
    typeof value.at === 'number'
  );
}

export const defaultBackoff = (attempts: number): number =>
  Math.min(BACKOFF_BASE_MS * 2 ** Math.max(0, attempts - 1), BACKOFF_MAX_MS);

type Failure =
  | { kind: 'offline' }
  | { kind: 'unauthorized' }
  | { kind: 'retry'; unexpected: boolean }
  /** 403/404: retried a few times before it is given up on. */
  | { kind: 'limited'; status: number; code: string }
  | { kind: 'drop'; status: number; code: string };

/** Reads `status`/`code` off whatever the sender threw (an `ApiClientError` has both). */
function classify(error: unknown): Failure {
  if (!isRecord(error) || typeof error.status !== 'number') {
    return { kind: 'retry', unexpected: true };
  }
  const { status } = error;
  const code = typeof error.code === 'string' ? error.code : 'http_error';
  if (status === 0) return { kind: 'offline' };
  if (status === 401) return { kind: 'unauthorized' };
  if (status === 403 || status === 404) return { kind: 'limited', status, code };
  if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
    return { kind: 'drop', status, code };
  }
  return { kind: 'retry', unexpected: false };
}

export function createAttemptQueue(options: AttemptQueueOptions): AttemptQueue {
  const { storage, send } = options;
  const now = options.now ?? Date.now;
  const backoff = options.backoff ?? defaultBackoff;
  const report = options.report ?? (() => undefined);

  const saved = new Set<string>();
  const purged = new Set<string>();
  const listeners = new Set<() => void>();
  const savedListeners = new Set<(attempt: AttemptInput) => void>();
  let version = 0;
  const inflight = new Map<string, Promise<FlushResult>>();
  /** Set when the stored value is unreadable: the queue then lives here and never writes storage. */
  let memory: Persisted | null = null;

  function read(): Persisted {
    if (memory !== null) return memory;
    const raw = storage.get(QUEUE_STORAGE_KEY);
    if (raw === null) return { v: 1, items: [], dead: [] };
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = undefined;
    }
    if (!isRecord(parsed) || parsed.v !== 1) {
      memory = { v: 1, items: [], dead: [] };
      report(new Error('attempt queue storage is unreadable: kept untouched, working in memory'));
      return memory;
    }
    return {
      v: 1,
      items: Array.isArray(parsed.items) ? (parsed.items as unknown[]).filter(isQueued) : [],
      dead: Array.isArray(parsed.dead) ? (parsed.dead as unknown[]).filter(isDead) : [],
    };
  }

  function write(next: Persisted): void {
    if (memory !== null) memory = next;
    else storage.set(QUEUE_STORAGE_KEY, JSON.stringify(next));
    version += 1;
    for (const listener of [...listeners]) listener();
  }

  const keyOf = (item: { readonly input: AttemptInput }): string => item.input.idempotencyKey;

  /** Drops what outlived the retention (see the module doc). `current`: the owner who is active. */
  function pruned(state: Persisted, current: string): Persisted {
    const limit = now() - RETENTION_MS;
    const items = state.items.filter((i) => i.owner === current || i.queuedAt > limit);
    const dead = state.dead.filter((d) => d.at > limit);
    return items.length === state.items.length && dead.length === state.dead.length
      ? state
      : { v: 1, items, dead };
  }

  function enqueue(input: AttemptInput, owner: string): boolean {
    const before = read();
    const state = pruned(before, owner);
    const key = input.idempotencyKey;
    if (saved.has(key) || purged.has(key)) return false;
    if (state.items.some((i) => keyOf(i) === key) || state.dead.some((d) => keyOf(d) === key)) {
      if (state !== before) write(state);
      return false;
    }
    const item: QueuedAttempt = { input, owner, attempts: 0, nextAt: 0, queuedAt: now() };
    const items = [...state.items, item];
    if (items.length > MAX_QUEUED) {
      items.splice(0, items.length - MAX_QUEUED);
      report(new Error('attempt queue full: oldest results dropped'));
    }
    write({ ...state, items });
    return true;
  }

  function toDead(state: Persisted, item: QueuedAttempt, status: number, code: string): Persisted {
    const dead: DeadAttempt = { input: item.input, owner: item.owner, status, code, at: now() };
    return {
      ...state,
      items: state.items.filter((i) => keyOf(i) !== keyOf(item)),
      dead: [...state.dead, dead].slice(-MAX_DEAD),
    };
  }

  async function run(owner: string): Promise<FlushResult> {
    let sent = 0;
    let dropped = 0;
    const first = read();
    const afterPrune = pruned(first, owner);
    if (afterPrune !== first) write(afterPrune);
    // Picks the next due item each time, so a result queued while this run is sending joins it.
    const tried = new Set<string>();
    const { session } = options;
    let verified = session === undefined;
    for (;;) {
      const item = read().items.find(
        (i) => i.owner === owner && i.nextAt <= now() && !tried.has(keyOf(i)),
      );
      if (item === undefined) break;
      if (!verified && session !== undefined) {
        // A tab that still holds user A must not send A's results once the cookie is user B's.
        const check = await session();
        if (check.kind === 'error') return { sent, dropped, stoppedBy: 'offline' };
        if (check.kind === 'anonymous' || check.id !== owner) {
          return { sent, dropped, stoppedBy: 'unauthorized' };
        }
        verified = true;
        continue;
      }
      const key = keyOf(item);
      tried.add(key);
      try {
        await send(item.input);
      } catch (error) {
        const failure = classify(error);
        if (failure.kind === 'offline') return { sent, dropped, stoppedBy: 'offline' };
        if (failure.kind === 'unauthorized') return { sent, dropped, stoppedBy: 'unauthorized' };
        const state = read();
        if (failure.kind === 'drop') {
          write(toDead(state, item, failure.status, failure.code));
          report(new Error(`attempt dropped: ${failure.status} ${failure.code}`));
          dropped += 1;
          continue;
        }
        const attempts = item.attempts + 1;
        if (failure.kind === 'limited' && attempts > MAX_CLIENT_ERROR_RETRIES) {
          write(toDead(state, item, failure.status, failure.code));
          report(new Error(`attempt dropped: ${failure.status} ${failure.code}`));
          dropped += 1;
          continue;
        }
        write({
          ...state,
          items: state.items.map((i) =>
            keyOf(i) === key ? { ...i, attempts, nextAt: now() + backoff(attempts) } : i,
          ),
        });
        if (failure.kind === 'retry' && failure.unexpected) report(error);
        return { sent, dropped, stoppedBy: 'backoff' };
      }
      saved.add(key);
      const state = read();
      write({ ...state, items: state.items.filter((i) => keyOf(i) !== key) });
      sent += 1;
      for (const listener of [...savedListeners]) listener(item.input);
    }
    return { sent, dropped, stoppedBy: null };
  }

  function purge(owner?: string): void {
    if (owner === undefined && memory === null) {
      // Everything goes, even a value this code cannot read.
      for (const i of read().items) purged.add(keyOf(i));
      storage.remove(QUEUE_STORAGE_KEY);
      memory = null;
      version += 1;
      for (const listener of [...listeners]) listener();
      return;
    }
    const state = read();
    const match = (o: string): boolean => owner === undefined || o === owner;
    for (const i of state.items) if (match(i.owner)) purged.add(keyOf(i));
    for (const d of state.dead) if (match(d.owner)) purged.add(keyOf(d));
    write({
      v: 1,
      items: state.items.filter((i) => !match(i.owner)),
      dead: state.dead.filter((d) => !match(d.owner)),
    });
  }

  return {
    enqueue,
    purge,
    flush(owner) {
      // Single-flight per owner: a run for another user is not joined, it starts on its own.
      const running = inflight.get(owner);
      if (running !== undefined) return running;
      const started = run(owner).finally(() => {
        inflight.delete(owner);
      });
      inflight.set(owner, started);
      return started;
    },
    subscribeSaved(listener) {
      savedListeners.add(listener);
      return () => void savedListeners.delete(listener);
    },
    statusOf(key) {
      if (saved.has(key)) return 'saved';
      if (purged.has(key)) return 'purged';
      const state = read();
      if (state.items.some((i) => keyOf(i) === key)) return 'queued';
      if (state.dead.some((d) => keyOf(d) === key)) return 'dead';
      return 'unknown';
    },
    pending(owner) {
      const { items } = read();
      return owner === undefined ? items : items.filter((i) => i.owner === owner);
    },
    dead: () => read().dead,
    nextDueIn(owner) {
      const mine = read().items.filter((i) => i.owner === owner);
      if (mine.length === 0) return null;
      return Math.max(0, Math.min(...mine.map((i) => i.nextAt)) - now());
    },
    version: () => version,
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}
